import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Settings, 
  Building2, 
  Users, 
  User,
  UserCog,
  FileText, 
  Calendar,
  MessageSquare,
  BarChart3,
  Puzzle,
  Palette,
  Globe,
  Clock,
  Shield,
  GraduationCap,
  Heart,
  Briefcase,
  Bell,
  Database,
  Workflow,
  List,
  Loader2,
  ChevronRight,
  Save,
  Check,
  MapPin,
  UtensilsCrossed,
  Upload,
  Wrench,
  ClipboardCheck,
  Plus,
  X,
  UserPlus,
  RefreshCw,
  ArrowRight,
  Mail,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { SnapEtBillingEncountersCard } from '../components/SnapEtBillingEncountersCard';
import { HelpConnectCourseDaysCard } from '../components/HelpConnectCourseDaysCard';
import { HelpConnectBulkAccessCard } from '../components/HelpConnectBulkAccessCard';
import { HelpConnectWelcomeCard, HelpConnectDeactivatedCard, HelpConnectAccessEndingCard } from '../components/HelpConnectWelcomeCard';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern Time (ET)' },
  { id: 'America/Chicago', label: 'Central Time (CT)' },
  { id: 'America/Denver', label: 'Mountain Time (MT)' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Anchorage', label: 'Alaska Time' },
  { id: 'Pacific/Honolulu', label: 'Hawaii Time' },
];

const DATE_FORMATS = [
  { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
];

const TIME_FORMATS = [
  { id: '12h', label: '12-hour (1:30 PM)' },
  { id: '24h', label: '24-hour (13:30)' },
];

const SettingsDashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'organization');
  const [systemSettings, setSystemSettings] = useState({
    organization_name: 'MyHELP',
    organization_tagline: 'Case Management System',
    primary_color: '#92298d',
    timezone: 'America/Chicago',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    fiscal_year_start: '01',
    default_state: 'TN',
    default_office: '',
    default_program: '',
    ai_enabled: true,
    ai_auto_summaries: true,
    ai_case_notes: true
  });
  const [offices, setOffices] = useState([]);
  const [programs, setPrograms] = useState([]);
  
  // SNAP E&T Escalation Contact state
  const [snapEtEscalationContact, setSnapEtEscalationContact] = useState(null);
  const [loadingSnapEtSettings, setLoadingSnapEtSettings] = useState(false);
  const [savingSnapEtSettings, setSavingSnapEtSettings] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedEscalationUserId, setSelectedEscalationUserId] = useState('');
  
  // SNAP E&T Alert Messages state
  const [alertMessages, setAlertMessages] = useState(null);
  const [certificationMessage, setCertificationMessage] = useState('');
  const [enrollmentMessage, setEnrollmentMessage] = useState('');
  const [retentionMessage, setRetentionMessage] = useState('');
  const [savingAlertMessages, setSavingAlertMessages] = useState(false);
  
  // MTAJ-NC Backfill state
  const [runningMtajBackfill, setRunningMtajBackfill] = useState(false);
  const [mtajBackfillResults, setMtajBackfillResults] = useState(null);
  const [showMtajBackfillDialog, setShowMtajBackfillDialog] = useState(false);
  
  // County Backfill state
  const [runningCountyBackfill, setRunningCountyBackfill] = useState(false);
  const [countyBackfillResults, setCountyBackfillResults] = useState(null);
  const [showCountyBackfillDialog, setShowCountyBackfillDialog] = useState(false);
  const [countyStatus, setCountyStatus] = useState(null);
  const [loadingCountyStatus, setLoadingCountyStatus] = useState(false);
  
  // Caseload Review Duplicate Cleanup state
  const [runningCaseloadCleanup, setRunningCaseloadCleanup] = useState(false);
  const [caseloadCleanupResults, setCaseloadCleanupResults] = useState(null);
  
  // Caseload Review Completion Notification state
  const [caseloadReviewNotification, setCaseloadReviewNotification] = useState({
    enabled: false,
    recipients: [],
    recipient_roles: [],
    custom_message: ''
  });
  const [loadingCaseloadReviewSettings, setLoadingCaseloadReviewSettings] = useState(false);
  const [savingCaseloadReviewSettings, setSavingCaseloadReviewSettings] = useState(false);
  
  // Information Dissemination Notification state
  const [infoDisseminationSettings, setInfoDisseminationSettings] = useState({
    enabled: false,
    email_message: '',
    sms_message: ''
  });
  const [loadingInfoDisseminationSettings, setLoadingInfoDisseminationSettings] = useState(false);
  const [savingInfoDisseminationSettings, setSavingInfoDisseminationSettings] = useState(false);
  
  // SNAP E&T Auto-Assignment state
  const [autoAssignmentEnabled, setAutoAssignmentEnabled] = useState(false);
  const [autoAssignmentAssignments, setAutoAssignmentAssignments] = useState({});
  const [autoAssignmentCaseManagers, setAutoAssignmentCaseManagers] = useState({});
  const [availableCaseManagers, setAvailableCaseManagers] = useState([]);
  const [snapEtEnrollmentTypes, setSnapEtEnrollmentTypes] = useState([]);
  const [loadingAutoAssignment, setLoadingAutoAssignment] = useState(false);
  const [savingAutoAssignment, setSavingAutoAssignment] = useState(false);
  const [addingCMTo, setAddingCMTo] = useState(null);
  const [selectedCM, setSelectedCM] = useState('');
  
  // Case Transfer state
  const [transferStaff, setTransferStaff] = useState([]);
  const [unassignedCaseCount, setUnassignedCaseCount] = useState(0);
  const [fromStaffId, setFromStaffId] = useState('');
  const [toStaffId, setToStaffId] = useState('');
  const [transferProgramType, setTransferProgramType] = useState('_all');
  const [transferReason, setTransferReason] = useState('');
  const [sendTransferNotification, setSendTransferNotification] = useState(true);
  const [previewCases, setPreviewCases] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [showTransferConfirmDialog, setShowTransferConfirmDialog] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [loadingTransferStaff, setLoadingTransferStaff] = useState(false);
  
  // Settings categories for the dashboard cards
  const settingsCategories = [
    {
      id: 'organization',
      title: 'Organization Settings',
      description: 'Branding, timezone, date formats, and general organization info',
      icon: Building2,
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30',
      items: ['Organization Name', 'Branding Colors', 'Timezone', 'Date/Time Formats', 'Fiscal Year'],
      links: [
        { label: 'Dashboard Banners', href: '/settings/dashboard-banners' },
        { label: 'Agency Holidays', href: '/settings/agency-holidays' },
        { label: 'Email Footer', href: '/settings/email-footer' },
        { label: 'Team Pulse TV', href: '/settings/pulse' }
      ]
    },
    {
      id: 'users',
      title: 'Users & Security',
      description: 'User management, roles, permissions, and security policies',
      icon: UserCog,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30',
      links: [
        { label: 'User Accounts', href: '/users' },
        { label: 'Roles & Permissions', href: '/admin/roles' },
        { label: 'Security Settings', href: '/settings/security' }
      ]
    },
    {
      id: 'participants',
      title: 'Participant Settings',
      description: 'Intake fields, demographics, statuses, and participant configuration',
      icon: Users,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30',
      links: [
        { label: 'Dropdowns & Lookups', href: '/admin/dropdowns' }
      ]
    },
    {
      id: 'programs',
      title: 'Program Settings',
      description: 'Programs, enrollments, funding sources, and case types',
      icon: Briefcase,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30',
      links: [
        { label: 'Enrollment Types', href: '/admin/enrollment-types' },
        { label: 'Funding Sources', href: '/admin/funding-sources' },
        { label: 'Contracts', href: '/admin/contracts' },
        { label: 'Service Mappings', href: '/admin/service-mappings' }
      ]
    },
    {
      id: 'courses',
      title: 'Course Management',
      description: 'HELP Institute courses, certifications, and training programs',
      icon: GraduationCap,
      color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30',
      link: '/admin/courses'
    },
    {
      id: 'services',
      title: 'Supportive Services',
      description: 'Service types, funding limits, vendors, and approval workflows',
      icon: Heart,
      color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30',
      links: [
        { label: 'Service Types', href: '/admin/service-types' },
        { label: 'Program Types', href: '/admin/program-types' }
      ]
    },
    {
      id: 'cases',
      title: 'Case Management',
      description: 'Case statuses, priorities, closure rules, and workflow settings',
      icon: FileText,
      color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30',
      link: '/admin/case-settings'
    },
    {
      id: 'communications',
      title: 'Communications',
      description: 'RingCentral, email settings, templates, and notifications',
      icon: MessageSquare,
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30',
      links: [
        { label: 'Outbound Numbers', href: '/admin/outbound-numbers' },
        { label: 'Message Templates', href: '/templates' },
        { label: 'Email Designer Templates', href: '/settings/email-designer' },
        { label: 'Jotforms', href: '/admin/jotforms' },
        { label: 'HELP Messages', href: '/admin/messenger-settings' },
        { label: 'Webhook Diagnostics', href: '/admin/webhook-diagnostics' }
      ]
    },
    {
      id: 'appointments',
      title: 'Appointments',
      description: 'Appointment types, durations, SMS reminders, and scheduling settings',
      icon: Calendar,
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30',
      link: '/settings/appointments',
      items: ['Appointment Types', 'Durations', 'SMS Reminders', 'Reminder Triggers']
    },
    {
      id: 'documents',
      title: 'Documents & Templates',
      description: 'Document templates, message templates, Jotforms, merge fields, and retention policies',
      icon: FileText,
      color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30',
      link: '/admin/documents',
      subLinks: [
        { name: 'Document Settings', href: '/admin/documents' },
        { name: 'Message Templates', href: '/templates' },
        { name: 'Task Templates', href: '/admin/task-templates' },
        { name: 'Custom Merge Fields', href: '/settings/merge-fields' },
        { name: 'Jotforms', href: '/admin/jotforms' },
        { name: 'Template Analytics', href: '/templates/analytics' }
      ]
    },
    {
      id: 'ai',
      title: 'AI & Knowledge Center',
      description: 'AI models, prompts, automatic summaries, and knowledge resources',
      icon: Puzzle,
      color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30',
      link: '/admin/ai-settings'
    },
    {
      id: 'workflows',
      title: 'Workflows & Automation',
      description: 'Triggers, automatic tasks, notifications, and approval workflows',
      icon: Workflow,
      color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30',
      link: '/admin/workflows'
    },
    {
      id: 'dropdowns',
      title: 'Dropdowns & Lookups',
      description: 'Manage all dropdown values, categories, tags, and lookup lists',
      icon: List,
      color: 'bg-lime-100 text-lime-600 dark:bg-lime-900/30',
      link: '/admin/dropdowns'
    },
    {
      id: 'reporting',
      title: 'Reporting & Dashboards',
      description: 'Dashboard widgets, KPIs, report templates, and export settings',
      icon: BarChart3,
      color: 'bg-sky-100 text-sky-600 dark:bg-sky-900/30',
      link: '/admin/reporting-settings'
    },
    {
      id: 'integrations',
      title: 'System Integrations',
      description: 'Microsoft 365, RingCentral, Jotform API, Wix Online Programs, and API configuration',
      icon: Puzzle,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30',
      link: '/admin/integrations',
      subLinks: [
        { name: 'All Integrations', href: '/admin/integrations' },
        { name: 'Wix Online Programs', href: '/admin/integrations/wix' }
      ]
    },
    {
      id: 'offices',
      title: 'Office Locations',
      description: 'Manage office locations and regional settings',
      icon: MapPin,
      color: 'bg-red-100 text-red-600 dark:bg-red-900/30',
      link: '/admin/offices'
    },
    {
      id: 'food-pantry',
      title: 'Food Pantry',
      description: 'Kiosk check-in, lobby display, and food pantry settings',
      icon: UtensilsCrossed,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30',
      link: '/admin/food-pantry-settings'
    },
    {
      id: 'data-tools',
      title: 'Data Recovery Tools',
      description: 'Restore missing documents and recover lost data',
      icon: Database,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30',
      link: '/admin/restore-documents'
    },
    {
      id: 'mass-upload',
      title: 'Mass Document Upload',
      description: 'Upload multiple participant documents at once with automatic matching',
      icon: Upload,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30',
      link: '/admin/mass-upload'
    },
    {
      id: 'token-repair',
      title: 'Token Repair Tool',
      description: 'Find and fix case notes with unresolved merge tokens from Mass Case Note Entry',
      icon: Wrench,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30',
      link: '/admin/token-repair',
      adminOnly: true
    }
  ];

  useEffect(() => {
    fetchSystemSettings();
    fetchOffices();
    fetchPrograms();
    fetchSnapEtSettings();
    fetchAllUsers();
    fetchAlertMessages();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      if (response.data) {
        setSystemSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await axios.get(`${API}/admin/offices`);
      setOffices(response.data.offices || []);
    } catch (error) {
      console.error('Failed to fetch offices:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      setPrograms(response.data.enrollment_types || []);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    }
  };

  const fetchSnapEtSettings = async () => {
    setLoadingSnapEtSettings(true);
    try {
      const response = await axios.get(`${API}/settings/snap-et-escalation-contact`);
      setSnapEtEscalationContact(response.data);
      setSelectedEscalationUserId(response.data.configured_user_id || '');
    } catch (error) {
      console.error('Failed to fetch SNAP E&T settings:', error);
    } finally {
      setLoadingSnapEtSettings(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter for active users who could receive escalations
      const activeUsers = (response.data.items || response.data || []).filter(u => u.is_active !== false);
      setAllUsers(activeUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSaveSnapEtEscalation = async () => {
    if (!selectedEscalationUserId) {
      toast.error('Please select an escalation contact');
      return;
    }
    
    setSavingSnapEtSettings(true);
    try {
      await axios.put(`${API}/settings/snap-et-escalation-contact`, {
        user_id: selectedEscalationUserId
      });
      toast.success('SNAP E&T Escalation Contact updated');
      fetchSnapEtSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update escalation contact');
    } finally {
      setSavingSnapEtSettings(false);
    }
  };

  const fetchAlertMessages = async () => {
    try {
      const response = await axios.get(`${API}/settings/snap-et-alert-messages`);
      setAlertMessages(response.data);
      setCertificationMessage(response.data.certification_period_message?.current_message || '');
      setEnrollmentMessage(response.data.enrollment_expiration_message?.current_message || '');
      setRetentionMessage(response.data.retention_expiration_message?.current_message || '');
    } catch (error) {
      console.error('Failed to fetch alert messages:', error);
    }
  };

  // Fetch SNAP E&T enrollment types dynamically
  const fetchSnapEtEnrollmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      const allTypes = response.data.enrollment_types || [];
      // Filter for SNAP E&T enrollment types (contain 'snap_et' or 'snap-et' but not 'outreach')
      const snapEtTypes = allTypes.filter(et => {
        const id = (et.id || '').toLowerCase();
        return (id.includes('snap_et') || id.includes('snap-et')) && !id.includes('outreach');
      });
      setSnapEtEnrollmentTypes(snapEtTypes);
    } catch (error) {
      console.error('Failed to fetch SNAP E&T enrollment types:', error);
    }
  };

  // Fetch Auto-Assignment settings
  const fetchAutoAssignmentSettings = async () => {
    setLoadingAutoAssignment(true);
    try {
      const response = await axios.get(`${API}/settings/snap-et-auto-assignment`);
      setAutoAssignmentEnabled(response.data.enabled || false);
      setAutoAssignmentAssignments(response.data.assignments || {});
      setAutoAssignmentCaseManagers(response.data.case_managers || {});
      setAvailableCaseManagers(response.data.available_case_managers || []);
    } catch (error) {
      console.error('Failed to load auto-assignment settings:', error);
      toast.error('Failed to load auto-assignment settings');
    } finally {
      setLoadingAutoAssignment(false);
    }
  };

  // Save Auto-Assignment settings
  const handleSaveAutoAssignment = async () => {
    setSavingAutoAssignment(true);
    try {
      await axios.put(`${API}/settings/snap-et-auto-assignment`, {
        enabled: autoAssignmentEnabled,
        assignments: autoAssignmentAssignments
      });
      toast.success('Auto-assignment settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAutoAssignment(false);
    }
  };

  // Add case manager to a program type
  const addCaseManagerToProgram = (programTypeId) => {
    if (!selectedCM) return;
    
    const currentAssigned = autoAssignmentAssignments[programTypeId] || [];
    if (currentAssigned.includes(selectedCM)) {
      toast.error('This case manager is already assigned to this program type');
      return;
    }
    
    // Get the case manager details
    const cmDetails = availableCaseManagers.find(cm => cm.id === selectedCM);
    if (cmDetails) {
      setAutoAssignmentCaseManagers(prev => ({
        ...prev,
        [selectedCM]: cmDetails
      }));
    }
    
    setAutoAssignmentAssignments(prev => ({
      ...prev,
      [programTypeId]: [...(prev[programTypeId] || []), selectedCM]
    }));
    
    setSelectedCM('');
    setAddingCMTo(null);
  };

  // Remove case manager from a program type
  const removeCaseManagerFromProgram = (programTypeId, cmId) => {
    setAutoAssignmentAssignments(prev => ({
      ...prev,
      [programTypeId]: (prev[programTypeId] || []).filter(id => id !== cmId)
    }));
  };

  // Get available CMs not yet assigned to a program type
  const getAvailableCMsForType = (programTypeId) => {
    const assigned = autoAssignmentAssignments[programTypeId] || [];
    return availableCaseManagers.filter(cm => !assigned.includes(cm.id));
  };

  // Fetch transfer-eligible staff
  const fetchTransferEligibleStaff = async () => {
    setLoadingTransferStaff(true);
    try {
      const response = await axios.get(`${API}/cases/transfer-eligible-staff`);
      setTransferStaff(response.data.staff || []);
      setUnassignedCaseCount(response.data.unassigned_case_count || 0);
    } catch (error) {
      console.error('Failed to load eligible staff:', error);
      toast.error('Failed to load staff list');
    } finally {
      setLoadingTransferStaff(false);
    }
  };

  // Fetch preview of cases to transfer
  const fetchTransferPreview = async () => {
    if (!fromStaffId) {
      toast.error('Please select the source case manager');
      return;
    }
    
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'open');
      if (fromStaffId === 'unassigned') {
        params.append('assigned_staff_id', 'null');
      } else {
        params.append('assigned_staff_id', fromStaffId);
      }
      if (transferProgramType && transferProgramType !== '_all') {
        params.append('program_type', transferProgramType);
      }
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/cases?${params}`);
      setPreviewCases(response.data.items || []);
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load case preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle case transfer
  const handleCaseTransfer = async () => {
    if (!fromStaffId || !toStaffId) {
      toast.error('Please select both source and target case managers');
      return;
    }
    
    setTransferring(true);
    setShowTransferConfirmDialog(false);
    
    try {
      const payload = {
        from_staff_id: fromStaffId,
        to_staff_id: toStaffId,
        reason: transferReason,
        send_notification: sendTransferNotification
      };
      
      if (transferProgramType && transferProgramType !== '_all') {
        payload.program_types = [transferProgramType];
      }
      
      const response = await axios.post(`${API}/cases/bulk-transfer`, payload);
      setTransferResult(response.data);
      
      if (response.data.transferred_count > 0) {
        toast.success(`Successfully transferred ${response.data.transferred_count} cases`);
      } else {
        toast.info('No cases were transferred');
      }
      
      // Refresh staff list to update counts
      fetchTransferEligibleStaff();
      setPreviewCases([]);
      
    } catch (error) {
      console.error('Failed to transfer cases:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer cases');
    } finally {
      setTransferring(false);
    }
  };

  // Helper functions for transfer
  const getTransferStaffName = (staffId) => {
    if (staffId === 'unassigned') return 'Unassigned';
    const s = transferStaff.find(st => st.id === staffId);
    return s ? s.name : staffId;
  };

  const getTransferStaffCaseCount = (staffId) => {
    if (staffId === 'unassigned') return unassignedCaseCount;
    const s = transferStaff.find(st => st.id === staffId);
    return s ? s.open_case_count : 0;
  };

  const handleSaveAlertMessages = async () => {
    setSavingAlertMessages(true);
    try {
      await axios.put(`${API}/settings/snap-et-alert-messages`, {
        certification_period_message: certificationMessage,
        enrollment_expiration_message: enrollmentMessage,
        retention_expiration_message: retentionMessage
      });
      toast.success('Alert messages updated successfully');
      fetchAlertMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alert messages');
    } finally {
      setSavingAlertMessages(false);
    }
  };

  const handleResetCertificationMessage = () => {
    if (alertMessages?.certification_period_message?.default_message) {
      setCertificationMessage(alertMessages.certification_period_message.default_message);
    }
  };

  const handleResetEnrollmentMessage = () => {
    if (alertMessages?.enrollment_expiration_message?.default_message) {
      setEnrollmentMessage(alertMessages.enrollment_expiration_message.default_message);
    }
  };

  const handleResetRetentionMessage = () => {
    if (alertMessages?.retention_expiration_message?.default_message) {
      setRetentionMessage(alertMessages.retention_expiration_message.default_message);
    }
  };

  const handleRunMtajBackfill = async () => {
    setRunningMtajBackfill(true);
    setMtajBackfillResults(null);
    try {
      const response = await axios.post(`${API}/admin/backfill-mtaj-nc-close`);
      setMtajBackfillResults(response.data);
      
      if (response.data.closed_count > 0) {
        toast.success(`Successfully closed ${response.data.closed_count} MTAJ-NC Referral enrollment(s)`);
      } else if (response.data.already_closed_count > 0 && response.data.closed_count === 0) {
        toast.info('All eligible MTAJ-NC Referral enrollments were already closed');
      } else {
        toast.info('No MTAJ-NC Referral enrollments required closing');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run MTAJ-NC backfill');
      setMtajBackfillResults(null);
    } finally {
      setRunningMtajBackfill(false);
    }
  };

  // County Backfill functions
  const fetchCountyStatus = async () => {
    setLoadingCountyStatus(true);
    try {
      const response = await axios.get(`${API}/admin/participants/county-status`);
      setCountyStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch county status:', error);
    } finally {
      setLoadingCountyStatus(false);
    }
  };

  const handleRunCountyBackfill = async () => {
    setRunningCountyBackfill(true);
    setCountyBackfillResults(null);
    try {
      const response = await axios.post(`${API}/admin/participants/backfill-county`);
      setCountyBackfillResults(response.data);
      
      if (response.data.updated_count > 0) {
        toast.success(`Successfully updated county for ${response.data.updated_count} participant(s)`);
      } else if (response.data.total_candidates === 0) {
        toast.info('All participants already have county populated');
      } else {
        toast.info('No participants were updated');
      }
      
      // Refresh county status after backfill
      fetchCountyStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run county backfill');
      setCountyBackfillResults(null);
    } finally {
      setRunningCountyBackfill(false);
    }
  };

  // Caseload Review Duplicate Cleanup
  const handleRunCaseloadCleanup = async () => {
    setRunningCaseloadCleanup(true);
    setCaseloadCleanupResults(null);
    try {
      const response = await axios.post(`${API}/caseload-review/cleanup-duplicates`);
      setCaseloadCleanupResults(response.data);
      
      if (response.data.duplicates_removed > 0) {
        toast.success(`Successfully removed ${response.data.duplicates_removed} duplicate records from ${response.data.groups_processed} groups`);
      } else {
        toast.info('No duplicate records found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run duplicate cleanup');
      setCaseloadCleanupResults(null);
    } finally {
      setRunningCaseloadCleanup(false);
    }
  };

  // Fetch county status when admin tab is selected
  useEffect(() => {
    if (activeTab === 'admin' && user?.role === 'system_admin' && !countyStatus) {
      fetchCountyStatus();
    }
  }, [activeTab, user?.role]);
  
  // Fetch caseload review notification settings when snap-et tab is selected
  useEffect(() => {
    if (activeTab === 'snap-et' && user?.role === 'system_admin') {
      fetchCaseloadReviewNotificationSettings();
      fetchSnapEtEnrollmentTypes();
      fetchAutoAssignmentSettings();
      fetchTransferEligibleStaff();
    }
  }, [activeTab, user?.role]);

  // Fetch preview when from staff changes
  useEffect(() => {
    if (fromStaffId && activeTab === 'snap-et') {
      fetchTransferPreview();
    } else {
      setPreviewCases([]);
    }
  }, [fromStaffId, transferProgramType]);

  // Fetch info dissemination settings when automations tab is selected
  useEffect(() => {
    if (activeTab === 'automations' && user?.role === 'system_admin') {
      fetchInfoDisseminationSettings();
    }
  }, [activeTab, user?.role]);
  
  const fetchCaseloadReviewNotificationSettings = async () => {
    setLoadingCaseloadReviewSettings(true);
    try {
      const response = await axios.get(`${API}/settings/caseload-review-completion-notification`);
      setCaseloadReviewNotification(response.data);
    } catch (error) {
      console.error('Failed to fetch caseload review notification settings:', error);
    } finally {
      setLoadingCaseloadReviewSettings(false);
    }
  };
  
  const handleSaveCaseloadReviewNotificationSettings = async () => {
    setSavingCaseloadReviewSettings(true);
    try {
      await axios.put(`${API}/settings/caseload-review-completion-notification`, caseloadReviewNotification);
      toast.success('Caseload Review Completion Notification settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingCaseloadReviewSettings(false);
    }
  };
  
  const handleToggleCaseloadReviewRole = (role) => {
    setCaseloadReviewNotification(prev => {
      const roles = prev.recipient_roles || [];
      if (roles.includes(role)) {
        return { ...prev, recipient_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, recipient_roles: [...roles, role] };
      }
    });
  };
  
  const handleToggleCaseloadReviewRecipient = (userId) => {
    setCaseloadReviewNotification(prev => {
      const recipients = prev.recipients || [];
      if (recipients.includes(userId)) {
        return { ...prev, recipients: recipients.filter(id => id !== userId) };
      } else {
        return { ...prev, recipients: [...recipients, userId] };
      }
    });
  };
  
  // Fetch Information Dissemination Notification settings
  const fetchInfoDisseminationSettings = async () => {
    setLoadingInfoDisseminationSettings(true);
    try {
      const response = await axios.get(`${API}/settings/information-dissemination-notification`);
      setInfoDisseminationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch Information Dissemination settings:', error);
    } finally {
      setLoadingInfoDisseminationSettings(false);
    }
  };
  
  const handleSaveInfoDisseminationSettings = async () => {
    setSavingInfoDisseminationSettings(true);
    try {
      await axios.put(`${API}/settings/information-dissemination-notification`, infoDisseminationSettings);
      toast.success('Information Dissemination Notification settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingInfoDisseminationSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/system/settings`, systemSettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="w-8 h-8 text-primary" />
            Administration Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure and manage all aspects of MyHELP CRM
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="display">Display & Format</TabsTrigger>
          <TabsTrigger value="ai">AI Settings</TabsTrigger>
          <TabsTrigger value="automations">Automations</TabsTrigger>
          <TabsTrigger value="snap-et">SNAP E&T</TabsTrigger>
        </TabsList>

        {/* Overview Tab - All Categories */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {settingsCategories
              .filter(category => !category.adminOnly || user?.role === 'system_admin')
              .map((category) => {
              const Icon = category.icon;
              return (
                <Card 
                  key={category.id} 
                  className="card-shadow hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{category.title}</CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {category.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {category.subLinks ? (
                      <div className="space-y-2">
                        <Link to={category.link}>
                          <Button variant="outline" size="sm" className="w-full mb-2">
                            Manage All <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                        <div className="flex flex-wrap gap-2">
                          {category.subLinks.map((subLink, i) => (
                            <Link key={i} to={subLink.href}>
                              <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
                                {subLink.name}
                              </Badge>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : category.links ? (
                      <div className="flex flex-wrap gap-2">
                        {category.links.map((link, i) => (
                          <Link key={i} to={link.href} data-testid={`settings-link-${link.href.replace(/\//g, '-').slice(1)}`}>
                            <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
                              {link.label}
                            </Badge>
                          </Link>
                        ))}
                        {category.items?.slice(0, 4).map((item, i) => (
                          <Badge key={`i${i}`} variant="outline" className="text-xs">{item}</Badge>
                        ))}
                      </div>
                    ) : category.link ? (
                      <Link to={category.link}>
                        <Button variant="outline" size="sm" className="w-full">
                          Manage <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    ) : category.items ? (
                      <div className="flex flex-wrap gap-1">
                        {category.items.slice(0, 4).map((item, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-6">
          <Card className="card-shadow" data-testid="org-dashboard-banners-card">
            <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold">Dashboard Banners</div>
                <p className="text-sm text-muted-foreground">Upload holiday or campaign banners and greetings for the MyHELP dashboard.</p>
              </div>
              <Button asChild variant="outline" size="sm" data-testid="org-manage-banners-btn">
                <Link to="/settings/dashboard-banners">Manage Banners <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </CardContent>
          </Card>
          <Card className="card-shadow" data-testid="org-agency-holidays-card">
            <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold">Agency Holidays</div>
                <p className="text-sm text-muted-foreground">Automatic texts are never sent on weekends or these dates.</p>
              </div>
              <Button asChild variant="outline" size="sm" data-testid="org-manage-holidays-btn">
                <Link to="/settings/agency-holidays">Manage Holidays <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </CardContent>
          </Card>
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Information
              </CardTitle>
              <CardDescription>
                Basic organization details and branding
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name">Organization Name</Label>
                  <Input
                    id="org_name"
                    value={systemSettings.organization_name}
                    onChange={(e) => setSystemSettings(prev => ({ ...prev, organization_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org_tagline">Tagline</Label>
                  <Input
                    id="org_tagline"
                    value={systemSettings.organization_tagline}
                    onChange={(e) => setSystemSettings(prev => ({ ...prev, organization_tagline: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Primary Brand Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary_color"
                      type="color"
                      value={systemSettings.primary_color}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={systemSettings.primary_color}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Default State</Label>
                  <Select
                    value={systemSettings.default_state}
                    onValueChange={(v) => setSystemSettings(prev => ({ ...prev, default_state: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TN">Tennessee</SelectItem>
                      <SelectItem value="NC">North Carolina</SelectItem>
                      <SelectItem value="IA">Iowa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fiscal Year Start</Label>
                  <Select
                    value={systemSettings.fiscal_year_start}
                    onValueChange={(v) => setSystemSettings(prev => ({ ...prev, fiscal_year_start: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="01">January</SelectItem>
                      <SelectItem value="04">April</SelectItem>
                      <SelectItem value="07">July</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Display & Format Tab */}
        <TabsContent value="display" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Date, Time & Localization
              </CardTitle>
              <CardDescription>
                Configure how dates and times are displayed throughout the system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={systemSettings.timezone}
                    onValueChange={(v) => setSystemSettings(prev => ({ ...prev, timezone: v }))}
                  >
                    <SelectTrigger>
                      <Globe className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.id} value={tz.id}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select
                    value={systemSettings.date_format}
                    onValueChange={(v) => setSystemSettings(prev => ({ ...prev, date_format: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_FORMATS.map(df => (
                        <SelectItem key={df.id} value={df.id}>{df.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Time Format</Label>
                  <Select
                    value={systemSettings.time_format}
                    onValueChange={(v) => setSystemSettings(prev => ({ ...prev, time_format: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_FORMATS.map(tf => (
                        <SelectItem key={tf.id} value={tf.id}>{tf.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Preview</p>
                <p className="text-muted-foreground">
                  Current date/time in {systemSettings.timezone}: {' '}
                  <span className="font-mono">
                    {new Date().toLocaleString('en-US', { 
                      timeZone: systemSettings.timezone,
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings Tab */}
        <TabsContent value="ai" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Puzzle className="w-5 h-5" />
                AI & Automation Settings
              </CardTitle>
              <CardDescription>
                Configure AI-powered features and automation rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Enable AI Features</p>
                    <p className="text-sm text-muted-foreground">Master switch for all AI functionality</p>
                  </div>
                  <Switch
                    checked={systemSettings.ai_enabled}
                    onCheckedChange={(v) => setSystemSettings(prev => ({ ...prev, ai_enabled: v }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Automatic Communication Summaries</p>
                    <p className="text-sm text-muted-foreground">Generate AI summaries for SMS, calls, and emails</p>
                  </div>
                  <Switch
                    checked={systemSettings.ai_auto_summaries}
                    onCheckedChange={(v) => setSystemSettings(prev => ({ ...prev, ai_auto_summaries: v }))}
                    disabled={!systemSettings.ai_enabled}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">AI-Assisted Case Notes</p>
                    <p className="text-sm text-muted-foreground">Generate case note drafts using AI</p>
                  </div>
                  <Switch
                    checked={systemSettings.ai_case_notes}
                    onCheckedChange={(v) => setSystemSettings(prev => ({ ...prev, ai_case_notes: v }))}
                    disabled={!systemSettings.ai_enabled}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations" className="space-y-6">
          {/* Information Dissemination Notification Settings - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow" data-testid="info-dissemination-notification-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Information Dissemination Notification
                </CardTitle>
                <CardDescription>
                  Automatically send email and SMS to participants when a SNAP Outreach case note with "Information Dissemination" in the subject is saved
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingInfoDisseminationSettings ? (
                  <div className="flex items-center gap-2 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-muted-foreground">Loading settings...</span>
                  </div>
                ) : (
                  <>
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Enable Automation</p>
                        <p className="text-sm text-muted-foreground">
                          Send email and SMS when SNAP Outreach case note subject contains "Information Dissemination"
                        </p>
                      </div>
                      <Switch
                        checked={infoDisseminationSettings.enabled}
                        onCheckedChange={(v) => setInfoDisseminationSettings(prev => ({ ...prev, enabled: v }))}
                        data-testid="info-dissemination-toggle"
                      />
                    </div>
                    
                    {infoDisseminationSettings.enabled && (
                      <div className="space-y-6">
                        {/* Info about how it works */}
                        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                          <h4 className="font-medium text-blue-900 mb-2">How It Works</h4>
                          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                            <li>Applies to <strong>SNAP Outreach</strong> cases only</li>
                            <li>When a case note with "Information Dissemination" in the subject is saved</li>
                            <li>The system sends an email AND SMS to the participant on that case</li>
                            <li>SMS is sent from RingCentral number <code className="px-1 bg-blue-100 rounded">980-486-9054</code></li>
                            <li>Both communications are logged on the participant's Communications tab</li>
                            <li>If participant is missing email or phone, that channel is skipped silently</li>
                          </ul>
                        </div>
                        
                        {/* Email Message */}
                        <div className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">Custom Email Message</Label>
                            <p className="text-sm text-muted-foreground mb-2">
                              Message to send via email. Use <code className="px-1 bg-muted rounded">[PARTICIPANT_NAME]</code> and <code className="px-1 bg-muted rounded">[CURRENT_DATE]</code> placeholders.
                            </p>
                          </div>
                          <Textarea
                            value={infoDisseminationSettings.email_message}
                            onChange={(e) => setInfoDisseminationSettings(prev => ({ ...prev, email_message: e.target.value }))}
                            placeholder="Enter the email message to send to participants..."
                            rows={4}
                            className="resize-none"
                            data-testid="info-dissemination-email-message"
                          />
                        </div>
                        
                        {/* SMS Message */}
                        <div className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">Custom SMS Message</Label>
                            <p className="text-sm text-muted-foreground mb-2">
                              Message to send via SMS. Keep it brief (160 characters recommended). Use <code className="px-1 bg-muted rounded">[PARTICIPANT_NAME]</code> and <code className="px-1 bg-muted rounded">[CURRENT_DATE]</code> placeholders.
                            </p>
                          </div>
                          <Textarea
                            value={infoDisseminationSettings.sms_message}
                            onChange={(e) => setInfoDisseminationSettings(prev => ({ ...prev, sms_message: e.target.value }))}
                            placeholder="Enter the SMS message to send to participants..."
                            rows={3}
                            className="resize-none"
                            data-testid="info-dissemination-sms-message"
                          />
                          <p className="text-xs text-muted-foreground">
                            Character count: {infoDisseminationSettings.sms_message?.length || 0} / 160 recommended
                          </p>
                        </div>
                        
                        {/* Save Button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={handleSaveInfoDisseminationSettings}
                            disabled={savingInfoDisseminationSettings}
                            data-testid="save-info-dissemination-btn"
                          >
                            {savingInfoDisseminationSettings ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Settings
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Info about Automations */}
          <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
            <h4 className="font-medium text-amber-900 mb-2">About Automations</h4>
            <p className="text-sm text-amber-800">
              Configure automated notifications and workflows that trigger based on specific actions in the system. These automations help streamline communication and reduce manual work.
            </p>
          </div>
        </TabsContent>

        {/* SNAP E&T Settings Tab */}
        <TabsContent value="snap-et" className="space-y-6">
          <Card className="card-shadow border-blue-200 bg-blue-50/40" data-testid="success-coaching-program-link-card">
            <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold">Success Coaching Weekly Program</p>
                <p className="text-sm text-muted-foreground">Set up to 12 weeks of topics, coaching questions, and the email and text sent after each Success Coaching Encounter note.</p>
              </div>
              <Button variant="outline" asChild data-testid="success-coaching-program-link">
                <Link to="/settings/success-coaching-program">Open Program Settings</Link>
              </Button>
            </CardContent>
          </Card>
          <SnapEtBillingEncountersCard />
          <HelpConnectCourseDaysCard />
          <HelpConnectBulkAccessCard />
          <HelpConnectWelcomeCard />
          <HelpConnectDeactivatedCard />
          <HelpConnectAccessEndingCard />
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                SNAP E&T Expiration Alert Settings
              </CardTitle>
              <CardDescription>
                Configure how SNAP E&T certification and enrollment expiration alerts are handled
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Escalation Contact */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Escalation Contact</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    When a Case Manager does not acknowledge an expiration alert within the allowed timeframe, 
                    the system will create a task and send an email notification to the escalation contact.
                  </p>
                </div>
                
                {loadingSnapEtSettings ? (
                  <div className="flex items-center gap-2 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-muted-foreground">Loading settings...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3 mb-3">
                        <User className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-medium">Current Escalation Contact</p>
                          {snapEtEscalationContact?.current_contact ? (
                            <p className="text-sm text-muted-foreground">
                              {snapEtEscalationContact.current_contact.name} ({snapEtEscalationContact.current_contact.email})
                              {snapEtEscalationContact.is_default && (
                                <Badge variant="secondary" className="ml-2">Default</Badge>
                              )}
                            </p>
                          ) : (
                            <p className="text-sm text-amber-600">Not configured - using system default</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {user?.role === 'system_admin' && (
                      <div className="space-y-3">
                        <Label>Change Escalation Contact</Label>
                        <div className="flex gap-3">
                          <Select
                            value={selectedEscalationUserId}
                            onValueChange={setSelectedEscalationUserId}
                          >
                            <SelectTrigger className="flex-1" data-testid="escalation-contact-select">
                              <SelectValue placeholder="Select a user..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allUsers
                                .filter(u => ['system_admin', 'executive', 'operations_manager'].includes(u.role))
                                .map(u => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.name || u.email} ({u.role?.replace('_', ' ')})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            onClick={handleSaveSnapEtEscalation}
                            disabled={savingSnapEtSettings || !selectedEscalationUserId}
                            data-testid="save-escalation-btn"
                          >
                            {savingSnapEtSettings ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Only System Admins, Executives, and Operations Managers can be set as escalation contacts.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Alert Messages Configuration */}
              {user?.role === 'system_admin' && (
                <div className="space-y-4 pt-6 border-t">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Alert Messages</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Customize the messages displayed in the expiration alert modals. Use the available tokens to include dynamic date information.
                    </p>
                  </div>

                  {/* Certification Period Alert Message */}
                  <div className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">Certification Period Expiration Alert</Label>
                      {alertMessages?.certification_period_message?.is_default === false && (
                        <Badge variant="outline" className="text-green-600 border-green-600">Customized</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                      <span className="text-purple-700 font-medium">Available Token:</span>
                      <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Certification Period End Date]</code>
                    </div>
                    <Textarea
                      value={certificationMessage}
                      onChange={(e) => setCertificationMessage(e.target.value)}
                      placeholder="Enter the alert message..."
                      rows={4}
                      className="resize-none"
                      data-testid="certification-alert-message"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetCertificationMessage}
                        className="text-muted-foreground"
                      >
                        Reset to Default
                      </Button>
                    </div>
                  </div>

                  {/* Enrollment Expiration Alert Message */}
                  <div className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">Program Enrollment Expiration Alert</Label>
                      {alertMessages?.enrollment_expiration_message?.is_default === false && (
                        <Badge variant="outline" className="text-green-600 border-green-600">Customized</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                      <span className="text-purple-700 font-medium">Available Token:</span>
                      <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Enrollment End Date]</code>
                    </div>
                    <Textarea
                      value={enrollmentMessage}
                      onChange={(e) => setEnrollmentMessage(e.target.value)}
                      placeholder="Enter the alert message..."
                      rows={4}
                      className="resize-none"
                      data-testid="enrollment-alert-message"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetEnrollmentMessage}
                        className="text-muted-foreground"
                      >
                        Reset to Default
                      </Button>
                    </div>
                  </div>

                  {/* Retention Expiration Alert Message */}
                  <div className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">Retention Expiration Alert</Label>
                      {alertMessages?.retention_expiration_message?.is_default === false && (
                        <Badge variant="outline" className="text-green-600 border-green-600">Customized</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                      <span className="text-purple-700 font-medium">Available Token:</span>
                      <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Retention End Date]</code>
                    </div>
                    <Textarea
                      value={retentionMessage}
                      onChange={(e) => setRetentionMessage(e.target.value)}
                      placeholder="Enter the alert message..."
                      rows={4}
                      className="resize-none"
                      data-testid="retention-alert-message"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetRetentionMessage}
                        className="text-muted-foreground"
                      >
                        Reset to Default
                      </Button>
                    </div>
                  </div>

                  {/* Save Alert Messages Button */}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveAlertMessages}
                      disabled={savingAlertMessages}
                      data-testid="save-alert-messages-btn"
                    >
                      {savingAlertMessages ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Alert Messages
                    </Button>
                  </div>
                </div>
              )}

              {/* Info about how alerts work */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-2">How SNAP E&T Expiration Alerts Work</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Alerts appear when viewing a participant with an upcoming SNAP E&T certification or enrollment expiration</li>
                  <li>Only the assigned Case Manager can acknowledge the alert</li>
                  <li>Other users can bypass the alert to continue viewing the participant</li>
                  <li>Unacknowledged alerts trigger automatic tasks and email escalations</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Caseload Review Completion Notification Settings - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow" data-testid="caseload-review-notification-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" />
                  Caseload Review Completion Notifications
                </CardTitle>
                <CardDescription>
                  Configure email notifications sent when a caseload review is marked complete
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingCaseloadReviewSettings ? (
                  <div className="flex items-center gap-2 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-muted-foreground">Loading settings...</span>
                  </div>
                ) : (
                  <>
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Enable Notifications</p>
                        <p className="text-sm text-muted-foreground">Send email when a caseload review is marked complete</p>
                      </div>
                      <Switch
                        checked={caseloadReviewNotification.enabled}
                        onCheckedChange={(v) => setCaseloadReviewNotification(prev => ({ ...prev, enabled: v }))}
                        data-testid="caseload-review-notification-toggle"
                      />
                    </div>
                    
                    {caseloadReviewNotification.enabled && (
                      <div className="space-y-6">
                        {/* Recipient Roles */}
                        <div className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">Notify Users by Role</Label>
                            <p className="text-sm text-muted-foreground">All active users with selected roles will receive notifications</p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {['system_admin', 'executive', 'operations_manager', 'case_manager', 'supervisor'].map(role => (
                              <div key={role} className="flex items-center space-x-2 p-2 border rounded">
                                <input
                                  type="checkbox"
                                  id={`role-${role}`}
                                  checked={(caseloadReviewNotification.recipient_roles || []).includes(role)}
                                  onChange={() => handleToggleCaseloadReviewRole(role)}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <label htmlFor={`role-${role}`} className="text-sm capitalize">
                                  {role.replace('_', ' ')}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Specific Recipients */}
                        <div className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">Additional Recipients</Label>
                            <p className="text-sm text-muted-foreground">Select specific users to receive notifications</p>
                          </div>
                          <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                            {allUsers.filter(u => u.is_active !== false).map(u => (
                              <div key={u.id} className="flex items-center space-x-2 p-2 hover:bg-muted rounded">
                                <input
                                  type="checkbox"
                                  id={`user-${u.id}`}
                                  checked={(caseloadReviewNotification.recipients || []).includes(u.id)}
                                  onChange={() => handleToggleCaseloadReviewRecipient(u.id)}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <label htmlFor={`user-${u.id}`} className="text-sm flex-1">
                                  {u.name} <span className="text-muted-foreground">({u.email})</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Custom Message */}
                        <div className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">Custom Email Message</Label>
                            <p className="text-sm text-muted-foreground mb-2">
                              Customize the email body. Use placeholders for dynamic content.
                            </p>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm flex-wrap">
                            <span className="text-blue-700 font-medium">Available Placeholders:</span>
                            <code className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">[WEEK_OF]</code>
                            <code className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">[PROGRAM]</code>
                            <code className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">[CASE_MANAGER]</code>
                            <code className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">[COMPLETED_BY]</code>
                            <code className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">[COMPLETED_AT]</code>
                          </div>
                          <Textarea
                            value={caseloadReviewNotification.custom_message}
                            onChange={(e) => setCaseloadReviewNotification(prev => ({ ...prev, custom_message: e.target.value }))}
                            placeholder="Leave empty to use default message. Example: The caseload review for [PROGRAM] (Week of [WEEK_OF]) has been completed by [COMPLETED_BY]."
                            rows={4}
                            className="resize-none"
                            data-testid="caseload-review-custom-message"
                          />
                        </div>
                        
                        {/* Save Button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={handleSaveCaseloadReviewNotificationSettings}
                            disabled={savingCaseloadReviewSettings}
                            data-testid="save-caseload-review-notification-btn"
                          >
                            {savingCaseloadReviewSettings ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Notification Settings
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* MTAJ-NC Referral Backfill Tool - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  MTAJ-NC Referral Backfill Tool
                </CardTitle>
                <CardDescription>
                  Close MTAJ-NC Referral enrollments when a participant has been subsequently enrolled in SNAP E&T Program - North Carolina
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
                  <h4 className="font-medium text-amber-900 mb-2">What This Tool Does</h4>
                  <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                    <li>Finds participants with both MTAJ-NC Referral AND SNAP E&T Program - North Carolina enrollments</li>
                    <li>Only closes MTAJ-NC Referral if the SNAP E&T NC enrollment was created <strong>after</strong> the MTAJ-NC Referral</li>
                    <li>Sets the MTAJ-NC close date to match the SNAP E&T NC case open date</li>
                    <li>Does NOT affect any SNAP E&T enrollments or other programs</li>
                  </ul>
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => setShowMtajBackfillDialog(true)}
                    disabled={runningMtajBackfill}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="run-mtaj-backfill-btn"
                  >
                    {runningMtajBackfill ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Running Backfill...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Run MTAJ-NC Referral Backfill
                      </>
                    )}
                  </Button>
                </div>

                {/* Results Display */}
                {mtajBackfillResults && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 border rounded-lg bg-gray-50 text-center">
                        <p className="text-2xl font-bold text-gray-900">{mtajBackfillResults.total_mtaj_nc_referrals}</p>
                        <p className="text-sm text-muted-foreground">Total MTAJ-NC Referrals</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-green-50 text-center">
                        <p className="text-2xl font-bold text-green-600">{mtajBackfillResults.closed_count}</p>
                        <p className="text-sm text-muted-foreground">Closed This Run</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-blue-50 text-center">
                        <p className="text-2xl font-bold text-blue-600">{mtajBackfillResults.already_closed_count}</p>
                        <p className="text-sm text-muted-foreground">Already Closed</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-amber-50 text-center">
                        <p className="text-2xl font-bold text-amber-600">{mtajBackfillResults.skipped_count}</p>
                        <p className="text-sm text-muted-foreground">Skipped</p>
                      </div>
                    </div>

                    {/* Closed Details */}
                    {mtajBackfillResults.closed_details?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-green-100 px-4 py-2 border-b">
                          <h4 className="font-medium text-green-800">Successfully Closed ({mtajBackfillResults.closed_details.length})</h4>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left">Participant ID</th>
                                <th className="px-4 py-2 text-left">MTAJ Enrollment</th>
                                <th className="px-4 py-2 text-left">SNAP E&T Enrollment</th>
                                <th className="px-4 py-2 text-left">Close Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {mtajBackfillResults.closed_details.map((item, idx) => (
                                <tr key={idx} className="hover:bg-muted/50 transition-colors">
                                  <td className="px-4 py-2 font-mono text-xs">{item.participant_id}</td>
                                  <td className="px-4 py-2 font-mono text-xs">{item.mtaj_enrollment_id}</td>
                                  <td className="px-4 py-2 font-mono text-xs">{item.snap_et_enrollment_id}</td>
                                  <td className="px-4 py-2">{formatDate(item.close_date) || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Skipped Details */}
                    {mtajBackfillResults.skipped_details?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-amber-100 px-4 py-2 border-b">
                          <h4 className="font-medium text-amber-800">Skipped ({mtajBackfillResults.skipped_details.length})</h4>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left">Participant ID</th>
                                <th className="px-4 py-2 text-left">MTAJ Enrollment</th>
                                <th className="px-4 py-2 text-left">Reason</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {mtajBackfillResults.skipped_details.map((item, idx) => (
                                <tr key={idx} className="hover:bg-muted/50 transition-colors">
                                  <td className="px-4 py-2 font-mono text-xs">{item.participant_id}</td>
                                  <td className="px-4 py-2 font-mono text-xs">{item.mtaj_enrollment_id}</td>
                                  <td className="px-4 py-2 text-amber-700">{item.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* County Backfill Tool - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  County Backfill Tool
                </CardTitle>
                <CardDescription>
                  Automatically populate the County field for participants who have an address but are missing a county value
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-2">What This Tool Does</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Finds participants with a ZIP code but missing county information</li>
                    <li>Looks up the county using the participant's existing address (ZIP code, city, state)</li>
                    <li>Updates the County field on each applicable participant profile</li>
                    <li>Provides a detailed summary of updated and skipped records with reasons</li>
                  </ul>
                </div>

                {/* Current Status */}
                {loadingCountyStatus ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading county status...
                  </div>
                ) : countyStatus && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 border rounded-lg bg-gray-50 text-center">
                      <p className="text-xl font-bold text-gray-900">{countyStatus.total_participants}</p>
                      <p className="text-xs text-muted-foreground">Total Participants</p>
                    </div>
                    <div className="p-3 border rounded-lg bg-green-50 text-center">
                      <p className="text-xl font-bold text-green-600">{countyStatus.with_county}</p>
                      <p className="text-xs text-muted-foreground">With County</p>
                    </div>
                    <div className="p-3 border rounded-lg bg-amber-50 text-center">
                      <p className="text-xl font-bold text-amber-600">{countyStatus.backfill_candidates}</p>
                      <p className="text-xs text-muted-foreground">Need Backfill</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => setShowCountyBackfillDialog(true)}
                    disabled={runningCountyBackfill}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="run-county-backfill-btn"
                  >
                    {runningCountyBackfill ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Running Backfill...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-2" />
                        Run County Backfill
                      </>
                    )}
                  </Button>
                </div>

                {/* County Backfill Results */}
                {countyBackfillResults && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 border rounded-lg bg-gray-50 text-center">
                        <p className="text-2xl font-bold text-gray-900">{countyBackfillResults.total_candidates}</p>
                        <p className="text-sm text-muted-foreground">Total Candidates</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-green-50 text-center">
                        <p className="text-2xl font-bold text-green-600">{countyBackfillResults.updated_count}</p>
                        <p className="text-sm text-muted-foreground">Updated This Run</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-amber-50 text-center">
                        <p className="text-2xl font-bold text-amber-600">{countyBackfillResults.skipped_count}</p>
                        <p className="text-sm text-muted-foreground">Skipped</p>
                      </div>
                      <div className="p-4 border rounded-lg bg-red-50 text-center">
                        <p className="text-2xl font-bold text-red-600">{countyBackfillResults.error_count}</p>
                        <p className="text-sm text-muted-foreground">Errors</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* SNAP E&T Auto-Assignment - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-blue-600" />
                      SNAP E&T Auto-Assignment
                    </CardTitle>
                    <CardDescription>
                      Configure automatic case manager assignment for SNAP E&T enrollments based on caseload balancing
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={handleSaveAutoAssignment} 
                    disabled={savingAutoAssignment}
                    data-testid="save-auto-assignment-btn"
                  >
                    {savingAutoAssignment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingAutoAssignment ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <Label className="text-base font-medium">Enable Auto-Assignment</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          {autoAssignmentEnabled 
                            ? 'Auto-assignment is active. New SNAP E&T cases will be distributed based on caseload.' 
                            : 'Auto-assignment is disabled. Cases will be assigned to the creating user.'}
                        </p>
                      </div>
                      <Switch
                        checked={autoAssignmentEnabled}
                        onCheckedChange={setAutoAssignmentEnabled}
                        data-testid="auto-assignment-enabled-toggle"
                      />
                    </div>

                    {/* Assignment Configuration */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
                          <Users className="w-5 h-5 text-purple-600" />
                          Case Manager Assignments by Program Type
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Assign case managers to each SNAP E&T program type. When a new enrollment is created, the system will assign the case to the eligible case manager with the lowest current caseload.
                        </p>
                      </div>
                      
                      {snapEtEnrollmentTypes.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground bg-gray-50 rounded-lg">
                          <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No SNAP E&T enrollment types found.</p>
                          <p className="text-xs mt-1">
                            Configure enrollment types in Settings → Program Settings → Enrollment Types
                          </p>
                        </div>
                      ) : (
                        snapEtEnrollmentTypes.map((program) => (
                          <div key={program.id} className="border rounded-lg p-4" data-testid={`program-type-${program.id}`}>
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="font-semibold flex items-center gap-2">
                                  {program.name}
                                  <Badge variant="outline">{program.id}</Badge>
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  {(autoAssignmentAssignments[program.id] || []).length} case manager(s) assigned
                                </p>
                              </div>
                              {addingCMTo === program.id ? (
                                <div className="flex items-center gap-2">
                                  <Select value={selectedCM} onValueChange={setSelectedCM}>
                                    <SelectTrigger className="w-[250px]" data-testid={`select-cm-${program.id}`}>
                                      <SelectValue placeholder="Select case manager..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getAvailableCMsForType(program.id).map((cm) => (
                                        <SelectItem key={cm.id} value={cm.id}>
                                          {cm.name} ({cm.role === 'case_manager' ? 'Case Manager' : cm.role})
                                        </SelectItem>
                                      ))}
                                      {getAvailableCMsForType(program.id).length === 0 && (
                                        <SelectItem value="_none" disabled>No available case managers</SelectItem>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Button 
                                    size="sm" 
                                    onClick={() => addCaseManagerToProgram(program.id)}
                                    disabled={!selectedCM}
                                    data-testid={`add-cm-btn-${program.id}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => { setAddingCMTo(null); setSelectedCM(''); }}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setAddingCMTo(program.id)}
                                  data-testid={`add-cm-trigger-${program.id}`}
                                >
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Add Case Manager
                                </Button>
                              )}
                            </div>
                            
                            {/* Assigned Case Managers */}
                            {(autoAssignmentAssignments[program.id] || []).length === 0 ? (
                              <div className="text-center py-4 text-muted-foreground bg-gray-50 rounded-lg">
                                <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No case managers assigned</p>
                                <p className="text-xs mt-1">
                                  Cases will be assigned to the creating user
                                </p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(autoAssignmentAssignments[program.id] || []).map((cmId) => {
                                  const cm = autoAssignmentCaseManagers[cmId] || availableCaseManagers.find(c => c.id === cmId);
                                  return (
                                    <div 
                                      key={cmId} 
                                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                                      data-testid={`assigned-cm-${cmId}`}
                                    >
                                      <div>
                                        <p className="font-medium text-sm">{cm?.name || cmId}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {cm?.role === 'case_manager' ? 'Case Manager' : cm?.role}
                                          {cm?.office_location && ` • ${cm.office_location}`}
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                        onClick={() => removeCaseManagerFromProgram(program.id, cmId)}
                                        data-testid={`remove-cm-${cmId}`}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* How It Works */}
                    <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                      <h4 className="font-medium text-blue-900 mb-2">How Auto-Assignment Works</h4>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>When a new SNAP E&T enrollment is created, the system identifies the program type</li>
                        <li>It retrieves the eligible case managers configured for that program type</li>
                        <li>Current open case counts are compared across eligible case managers</li>
                        <li>The new case is automatically assigned to the case manager with the lowest caseload</li>
                      </ol>
                      <p className="text-sm text-blue-700 mt-2 italic">
                        If no case managers are configured for a program type, or auto-assignment is disabled, cases are assigned to the creating user.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Case Transfer Tool - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                  Case Transfer Tool
                </CardTitle>
                <CardDescription>
                  Bulk reassign cases when a case manager leaves or workload needs rebalancing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingTransferStaff ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Transfer Form */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* From Staff */}
                      <div className="space-y-2">
                        <Label>Transfer FROM <span className="text-red-500">*</span></Label>
                        <Select value={fromStaffId} onValueChange={setFromStaffId}>
                          <SelectTrigger data-testid="from-staff-select">
                            <SelectValue placeholder="Select source case manager..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">
                              Unassigned ({unassignedCaseCount} cases)
                            </SelectItem>
                            {transferStaff.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} ({s.open_case_count} cases)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fromStaffId && (
                          <p className="text-sm text-muted-foreground">
                            {getTransferStaffCaseCount(fromStaffId)} open cases
                          </p>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="hidden md:flex items-center justify-center">
                        <ArrowRight className="w-8 h-8 text-muted-foreground" />
                      </div>

                      {/* To Staff */}
                      <div className="space-y-2">
                        <Label>Transfer TO <span className="text-red-500">*</span></Label>
                        <Select value={toStaffId} onValueChange={setToStaffId}>
                          <SelectTrigger data-testid="to-staff-select">
                            <SelectValue placeholder="Select target case manager..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">
                              Unassign (remove assignment)
                            </SelectItem>
                            {transferStaff.filter(s => s.id !== fromStaffId).map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} ({s.open_case_count} cases)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {toStaffId && toStaffId !== 'unassigned' && (
                          <p className="text-sm text-muted-foreground">
                            Currently has {getTransferStaffCaseCount(toStaffId)} open cases
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Filter by Program Type (Optional)</Label>
                        <Select value={transferProgramType} onValueChange={setTransferProgramType}>
                          <SelectTrigger data-testid="program-type-filter">
                            <SelectValue placeholder="All Program Types" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_all">All Program Types</SelectItem>
                            {snapEtEnrollmentTypes.map((pt) => (
                              <SelectItem key={pt.id} value={pt.id}>
                                {pt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Reason for Transfer (Optional)</Label>
                        <Textarea
                          placeholder="e.g., Staff member leaving, workload rebalancing..."
                          value={transferReason}
                          onChange={(e) => setTransferReason(e.target.value)}
                          className="h-[38px] min-h-[38px]"
                          data-testid="transfer-reason"
                        />
                      </div>
                    </div>

                    {/* Notification Option */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <Checkbox
                        id="send-notification"
                        checked={sendTransferNotification}
                        onCheckedChange={setSendTransferNotification}
                        data-testid="send-notification-checkbox"
                      />
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-600" />
                        <Label htmlFor="send-notification" className="cursor-pointer">
                          Send email notification to receiving case manager
                        </Label>
                      </div>
                    </div>

                    {/* Preview */}
                    {fromStaffId && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold flex items-center gap-2">
                              <Briefcase className="w-5 h-5 text-green-600" />
                              Cases to Transfer
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {loadingPreview ? 'Loading...' : `${previewCases.length} cases will be transferred`}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchTransferPreview}
                            disabled={loadingPreview}
                          >
                            {loadingPreview ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        
                        {loadingPreview ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin" />
                          </div>
                        ) : previewCases.length === 0 ? (
                          <div className="text-center py-6 text-muted-foreground">
                            <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p>No cases found matching the criteria</p>
                          </div>
                        ) : (
                          <>
                            <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Case ID</TableHead>
                                    <TableHead>Participant</TableHead>
                                    <TableHead>Program</TableHead>
                                    <TableHead>Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {previewCases.slice(0, 30).map((c) => (
                                    <TableRow key={c.id}>
                                      <TableCell className="font-mono text-sm">{c.id}</TableCell>
                                      <TableCell>{c.participant_name}</TableCell>
                                      <TableCell>{c.program_name}</TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className="bg-green-50 text-green-700">
                                          {c.status}
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            {previewCases.length > 30 && (
                              <p className="text-sm text-muted-foreground mt-2 text-center">
                                Showing first 30 of {previewCases.length} cases
                              </p>
                            )}

                            {/* Transfer Button */}
                            <div className="flex justify-end mt-4">
                              <Button
                                onClick={() => setShowTransferConfirmDialog(true)}
                                disabled={!toStaffId || transferring || previewCases.length === 0}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="transfer-button"
                              >
                                {transferring ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Transferring...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Transfer {previewCases.length} Cases
                                  </>
                                )}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Transfer Result */}
                    {transferResult && (
                      <div className={`p-4 rounded-lg ${transferResult.transferred_count > 0 ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
                        <h4 className="font-semibold flex items-center gap-2 mb-2">
                          {transferResult.transferred_count > 0 ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          )}
                          Transfer Complete
                        </h4>
                        <p className="text-sm mb-2">{transferResult.message}</p>
                        {transferResult.transferred_count > 0 && (
                          <div className="text-sm space-y-1">
                            <p><strong>Transfer ID:</strong> {transferResult.transfer_id}</p>
                            <p><strong>Cases Transferred:</strong> {transferResult.transferred_count}</p>
                            {transferResult.failed_count > 0 && (
                              <p className="text-red-600"><strong>Failed:</strong> {transferResult.failed_count}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Transfer Confirmation Dialog */}
          <AlertDialog open={showTransferConfirmDialog} onOpenChange={setShowTransferConfirmDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Confirm Bulk Transfer
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>
                    You are about to transfer <strong>{previewCases.length} cases</strong> from{' '}
                    <strong>{getTransferStaffName(fromStaffId)}</strong> to{' '}
                    <strong>{getTransferStaffName(toStaffId)}</strong>.
                  </p>
                  {transferProgramType && transferProgramType !== '_all' && (
                    <p>
                      Filtered to: <strong>{snapEtEnrollmentTypes.find(p => p.id === transferProgramType)?.name}</strong>
                    </p>
                  )}
                  {sendTransferNotification && toStaffId !== 'unassigned' && (
                    <p className="text-blue-600">
                      An email notification will be sent to {getTransferStaffName(toStaffId)}.
                    </p>
                  )}
                  <p className="text-yellow-600 font-medium mt-4">
                    This action cannot be easily undone. Are you sure you want to proceed?
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCaseTransfer}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Transfer Cases
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Caseload Review Duplicate Cleanup Tool - System Admin Only */}
          {user?.role === 'system_admin' && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-orange-600" />
                  Caseload Review Duplicate Cleanup
                </CardTitle>
                <CardDescription>
                  Remove duplicate participant records from the weekly caseload review snapshots
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                  <h4 className="font-medium text-orange-900 mb-2">What This Tool Does</h4>
                  <ul className="text-sm text-orange-800 space-y-1 list-disc list-inside">
                    <li>Identifies duplicate participant records per caseload review snapshot</li>
                    <li>For each duplicate group, keeps the most recently created record</li>
                    <li>Deletes all other duplicates in the group</li>
                    <li>Creates a unique index to prevent future duplicates</li>
                  </ul>
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleRunCaseloadCleanup}
                    disabled={runningCaseloadCleanup}
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="run-caseload-cleanup-btn"
                  >
                    {runningCaseloadCleanup ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Running Cleanup...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Run Duplicate Cleanup
                      </>
                    )}
                  </Button>
                </div>

                {/* Cleanup Results */}
                {caseloadCleanupResults && (
                  <div className={`p-4 rounded-lg ${(caseloadCleanupResults.duplicate_headers_removed > 0 || caseloadCleanupResults.duplicate_records_removed > 0 || caseloadCleanupResults.records_from_duplicate_headers_removed > 0) ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      {(caseloadCleanupResults.duplicate_headers_removed > 0 || caseloadCleanupResults.duplicate_records_removed > 0) ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Check className="w-5 h-5 text-blue-600" />
                      )}
                      Cleanup Complete
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">{caseloadCleanupResults.message}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      <div className="p-3 border rounded-lg bg-white text-center">
                        <p className="text-xl font-bold text-orange-600">{caseloadCleanupResults.duplicate_headers_removed || 0}</p>
                        <p className="text-xs text-muted-foreground">Duplicate Headers</p>
                      </div>
                      <div className="p-3 border rounded-lg bg-white text-center">
                        <p className="text-xl font-bold text-red-600">{caseloadCleanupResults.records_from_duplicate_headers_removed || 0}</p>
                        <p className="text-xs text-muted-foreground">Records from Dup Headers</p>
                      </div>
                      <div className="p-3 border rounded-lg bg-white text-center">
                        <p className="text-xl font-bold text-amber-600">{caseloadCleanupResults.duplicate_records_removed || 0}</p>
                        <p className="text-xs text-muted-foreground">Duplicate Records</p>
                      </div>
                      <div className="p-3 border rounded-lg bg-white text-center">
                        <p className="text-xl font-bold text-green-600">{(caseloadCleanupResults.header_index_created && caseloadCleanupResults.record_index_created) ? 'Yes' : 'Partial'}</p>
                        <p className="text-xs text-muted-foreground">Indexes Created</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* MTAJ-NC Backfill Confirmation Dialog */}
          <Dialog open={showMtajBackfillDialog} onOpenChange={setShowMtajBackfillDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm MTAJ-NC Referral Backfill</DialogTitle>
                <DialogDescription>
                  This will close all MTAJ-NC Referral enrollments for participants who have been subsequently enrolled in SNAP E&T Program - North Carolina.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> This action cannot be undone. Only MTAJ-NC Referral enrollments that have a subsequent SNAP E&T NC enrollment will be closed.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowMtajBackfillDialog(false)}
                  disabled={runningMtajBackfill}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowMtajBackfillDialog(false);
                    handleRunMtajBackfill();
                  }}
                  disabled={runningMtajBackfill}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-mtaj-backfill-btn"
                >
                  {runningMtajBackfill ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    'Run Backfill'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* County Backfill Confirmation Dialog */}
          <Dialog open={showCountyBackfillDialog} onOpenChange={setShowCountyBackfillDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm County Backfill</DialogTitle>
                <DialogDescription>
                  This will look up and populate the County field for all participants who have an address but are missing county information.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>This operation will:</strong>
                  </p>
                  <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                    <li>Find participants with ZIP code but no county</li>
                    <li>Look up county using address information</li>
                    <li>Update the participant's County field</li>
                  </ul>
                </div>
                {countyStatus && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>{countyStatus.backfill_candidates}</strong> participant(s) will be processed.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowCountyBackfillDialog(false)}
                  disabled={runningCountyBackfill}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowCountyBackfillDialog(false);
                    handleRunCountyBackfill();
                  }}
                  disabled={runningCountyBackfill}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-county-backfill-btn"
                >
                  {runningCountyBackfill ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    'Run Backfill'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} disabled={saving} size="lg">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsDashboard;
